import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';

const MAX_ZOOM = 4;
const HIT_RADIUS_PX = 32; // matches the min 32px touch target requirement
const LABEL_DURATION_MS = 2000;
const SCROLL_DURATION_MS = 450;

const distanceBetweenTouches = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// Walks up from a node to find the nearest scrollable ancestor (the
// DashboardLayout <main> in practice). Returns null if none is found
// (nothing to auto-scroll in that case - callers fall back gracefully).
const findScrollParent = (el) => {
  let node = el?.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

// Custom rAF-driven smooth scroll so we get a reliable "finished" callback -
// native smooth scrollIntoView doesn't expose one, and we need to know
// exactly when it's done before starting the label's 2s fade timer.
const animateScrollTo = (container, targetTop, targetLeft, duration, onDone) => {
  const startTop = container.scrollTop;
  const startLeft = container.scrollLeft;
  const dTop = targetTop - startTop;
  const dLeft = targetLeft - startLeft;
  if (Math.abs(dTop) < 1 && Math.abs(dLeft) < 1) {
    onDone && onDone();
    return;
  }
  const startTime = performance.now();
  const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
  const step = (now) => {
    const elapsed = now - startTime;
    const t = Math.min(1, elapsed / duration);
    const eased = easeInOutQuad(t);
    container.scrollTop = startTop + dTop * eased;
    container.scrollLeft = startLeft + dLeft * eased;
    if (t < 1) {
      requestAnimationFrame(step);
    } else {
      onDone && onDone();
    }
  };
  requestAnimationFrame(step);
};

/**
 * CockpitMarkerStage - the image + tappable markers for one cockpit view.
 *
 * Sizing: the stage ALWAYS sizes from the image's own natural aspect ratio
 * (CSS aspect-ratio computed from the view's w/h, width: 100%) - never a
 * fixed or JS-measured height. This is what keeps the image from
 * collapsing to a sliver when the surrounding flex layout recalculates on
 * resize/orientation change.
 *
 * Touch handling: at normal (unzoomed) scale, a one-finger swipe scrolls
 * the page as usual (touch-action: pan-y pinch-zoom) and taps still select
 * markers. Two fingers pinch-zoom the image (custom JS zoom, not native
 * page zoom). Once zoomed in, a one-finger drag pans the image instead of
 * scrolling the page (touch-action: none while zoomed). Double-tap or the
 * "Reset zoom" button returns to normal scale and normal page scrolling.
 *
 * Selection: markers are children of the same transformed box as the
 * image, so they visually scale and pan together with it. Marker
 * selection uses a single nearest-point hit test (not per-marker DOM
 * hit-testing) so that when two markers' tap zones overlap, whichever one
 * is physically closest to the touch point wins. Whenever the selection
 * changes (locate icon, a link, or tapping a marker), the page smooth-
 * scrolls to centre the selected marker once the image has loaded; the
 * label's 2s fade timer only starts once that scroll has finished. The
 * selected marker's ring/glow itself never fades while selected - it
 * pulses continuously. The label also reappears if the already-selected
 * marker is tapped again, or scrolls into view from manual scrolling.
 */
export const CockpitMarkerStage = (props) => {
  const view = props.view;
  const controls = props.controls || [];
  const selectedControlId = props.selectedControlId;
  const onSelect = props.onSelect;
  const onDeselect = props.onDeselect;
  const highlightIds = props.highlightIds || [];
  const compact = props.compact; // smaller, thinner markers on narrow/mobile screens

  const [labelVisible, setLabelVisible] = useState(false);
  const prevSelectedRef = useRef(selectedControlId);
  const labelTimerRef = useRef(null);
  const pendingScrollIdRef = useRef(null);
  const isAutoScrollingRef = useRef(false);

  const stageInnerRef = useRef(null);
  const selectedMarkerRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const gestureRef = useRef({ mode: null, startDist: 0, startScale: 1, startPan: { x: 0, y: 0 }, startTouch: null, moved: false });
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [gestureActive, setGestureActive] = useState(false);

  const startLabelTimer = useCallback(() => {
    setLabelVisible(true);
    if (labelTimerRef.current) clearTimeout(labelTimerRef.current);
    labelTimerRef.current = setTimeout(() => setLabelVisible(false), LABEL_DURATION_MS);
  }, []);

  // Smooth-scrolls the page so the selected marker is centred on screen.
  // The label (and its 2s fade timer) only appears once this finishes.
  // autoScroll defaults to on; the small fixed-height preview embedded in
  // the Assistance panel (CockpitLocatePanel) opts out since it isn't the
  // main scrollable page and has no need to scroll itself.
  const autoScroll = props.autoScroll !== false;
  const scrollSelectedIntoCenter = useCallback(() => {
    if (!autoScroll) {
      startLabelTimer();
      return;
    }
    const marker = selectedMarkerRef.current;
    const container = marker ? findScrollParent(marker) : null;
    if (!marker || !container) {
      startLabelTimer();
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const markerRect = marker.getBoundingClientRect();

    const markerCenterY = markerRect.top + markerRect.height / 2 - containerRect.top + container.scrollTop;
    const markerCenterX = markerRect.left + markerRect.width / 2 - containerRect.left + container.scrollLeft;

    const targetTop = clamp(markerCenterY - containerRect.height / 2, 0, Math.max(0, container.scrollHeight - containerRect.height));
    const targetLeft = clamp(markerCenterX - containerRect.width / 2, 0, Math.max(0, container.scrollWidth - containerRect.width));

    isAutoScrollingRef.current = true;
    animateScrollTo(container, targetTop, targetLeft, SCROLL_DURATION_MS, () => {
      isAutoScrollingRef.current = false;
      startLabelTimer();
    });
  }, [startLabelTimer, autoScroll]);

  // Selection changed (locate icon, a link, or tapping a different marker):
  // hide the label immediately, then scroll-to-centre once the image has
  // loaded (now, or queued for when it finishes loading below).
  useEffect(() => {
    if (prevSelectedRef.current === selectedControlId) return;
    prevSelectedRef.current = selectedControlId;

    if (labelTimerRef.current) clearTimeout(labelTimerRef.current);
    setLabelVisible(false);

    if (!selectedControlId) return;

    if (imageLoaded) {
      scrollSelectedIntoCenter();
    } else {
      pendingScrollIdRef.current = selectedControlId;
    }
  }, [selectedControlId, imageLoaded, scrollSelectedIntoCenter]);

  // Catches up once a pending selection's image finishes loading.
  useEffect(() => {
    if (imageLoaded && pendingScrollIdRef.current && pendingScrollIdRef.current === selectedControlId) {
      pendingScrollIdRef.current = null;
      scrollSelectedIntoCenter();
    }
  }, [imageLoaded, selectedControlId, scrollSelectedIntoCenter]);

  // Reappear the label if the selected marker scrolls into view from the
  // user manually scrolling (ignored while our own auto-scroll is running,
  // which already triggers the label itself once it finishes).
  useEffect(() => {
    const marker = selectedMarkerRef.current;
    if (!marker || !selectedControlId) return;
    const container = findScrollParent(marker);
    const observer = new IntersectionObserver(
      (entries) => {
        if (isAutoScrollingRef.current) return;
        if (entries[0].isIntersecting) startLabelTimer();
      },
      { root: container || null, threshold: 0.6 }
    );
    observer.observe(marker);
    return () => observer.disconnect();
  }, [selectedControlId, startLabelTimer]);

  // Esc deselects on desktop.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onDeselect && onDeselect();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onDeselect]);

  // Reset zoom/pan + image-load tracking whenever the view (image) changes.
  useEffect(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
    setImageLoaded(false);
  }, [view?.id]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const clampPanValue = (nextScale, nextPan, rect) => {
    if (!rect) return { x: 0, y: 0 };
    const maxPanX = (rect.width * (nextScale - 1)) / 2 + 40;
    const maxPanY = (rect.height * (nextScale - 1)) / 2 + 40;
    return {
      x: clamp(nextPan.x, -maxPanX, maxPanX),
      y: clamp(nextPan.y, -maxPanY, maxPanY),
    };
  };

  const resetZoom = () => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      gestureRef.current = {
        mode: 'pinch',
        startDist: distanceBetweenTouches(e.touches[0], e.touches[1]),
        startScale: scale,
        startPan: pan,
        moved: false,
      };
      setGestureActive(true);
    } else if (e.touches.length === 1) {
      gestureRef.current = {
        mode: scale > 1.01 ? 'pan' : 'tap',
        startTouch: { x: e.touches[0].clientX, y: e.touches[0].clientY },
        startPan: pan,
        moved: false,
      };
    }
  };

  const handleTouchMove = (e) => {
    const g = gestureRef.current;
    const rect = stageInnerRef.current?.getBoundingClientRect();
    if (g.mode === 'pinch' && e.touches.length === 2) {
      e.preventDefault();
      const dist = distanceBetweenTouches(e.touches[0], e.touches[1]);
      const nextScale = clamp(g.startScale * (dist / (g.startDist || 1)), 1, MAX_ZOOM);
      setScale(nextScale);
      setPan(clampPanValue(nextScale, g.startPan, rect));
      g.moved = true;
    } else if (g.mode === 'pan' && e.touches.length === 1) {
      // Only zoomed-in panning hijacks the gesture - at normal scale this
      // branch is never reached (mode is 'tap'), so the browser's native
      // one-finger scroll (allowed via touch-action below) still works.
      e.preventDefault();
      const dx = e.touches[0].clientX - g.startTouch.x;
      const dy = e.touches[0].clientY - g.startTouch.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) g.moved = true;
      setPan(clampPanValue(scale, { x: g.startPan.x + dx, y: g.startPan.y + dy }, rect));
    }
  };

  const handleTouchEnd = () => {
    gestureRef.current.mode = null;
    setGestureActive(false);
  };

  const handleDoubleClick = () => {
    resetZoom();
  };

  if (!view) return null;
  const ar = view.w / view.h;
  // Contain the image's own aspect ratio within BOTH the available width
  // AND the available height, so a wide-but-short desktop window can't
  // force the box wider than its height allows (which, with the old
  // `width: 100%` + `maxHeight: 100%` combo, left width pinned at 100%
  // while height got capped - stretching/distorting the image, since
  // nothing then shrank width to match). Leaving width/height both auto
  // and capping both axes with max-width/max-height lets aspect-ratio
  // resolve whichever axis is actually binding, every time. On mobile
  // (fitContent, no flex-1 ancestor with a definite height) max-height:
  // 100% resolves against an indefinite parent and is a no-op, so this
  // is unchanged there.
  const stageStyle = { maxWidth: '100%', maxHeight: '100%', aspectRatio: ar };

  // Nearest-marker hit test: finds whichever control's centre is physically
  // closest to the click/tap point, within HIT_RADIUS_PX. This is what makes
  // "nearest marker wins" deterministic when tap zones overlap in dense areas.
  const handleStageClick = (e) => {
    const g = gestureRef.current;
    if (g.moved) {
      g.moved = false;
      return;
    }
    e.stopPropagation();

    const rect = stageInnerRef.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return;
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    let nearest = null;
    let nearestDist = Infinity;
    controls.forEach((c) => {
      const cx = (c.x / 100) * rect.width;
      const cy = (c.y / 100) * rect.height;
      const dist = Math.hypot(clickX - cx, clickY - cy);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = c;
      }
    });

    if (nearest && nearestDist <= HIT_RADIUS_PX) {
      if (nearest.id === selectedControlId) {
        // Re-tapping the already-selected marker: re-centre and reappear
        // the label (selectedControlId itself won't change, so the
        // selection-change effect above won't fire for this).
        scrollSelectedIntoCenter();
      } else {
        onSelect && onSelect(nearest.id);
      }
    } else {
      onDeselect && onDeselect();
    }
  };

  const markerBaseSize = compact ? 14 : 18;
  const markerBaseBorder = compact ? 1.5 : 2;

  // In the stacked mobile layout the stage must hug the image's own
  // aspect-ratio size (never stretch to fill leftover flex space and never
  // get squeezed below that size either) - that's what fixes both the
  // "collapses to a sliver" bug and the "huge empty grid area" issue. In
  // the desktop side-by-side grid, stretching to fill the grid row/column
  // is fine (the grid track is generous and stretch is the expected look).
  const outerClassName = props.fitContent
    ? 'relative rounded-gc-card border flex items-center justify-center p-4 box-border overflow-hidden'
    : 'flex-1 min-h-0 relative rounded-gc-card border flex items-center justify-center p-4 box-border overflow-hidden';

  return (
    <div
      className={outerClassName}
      style={{
        borderColor: 'var(--gc-border-nav)',
        backgroundColor: '#070B11',
        backgroundImage: 'linear-gradient(#0E1520 1px, transparent 1px), linear-gradient(90deg, #0E1520 1px, transparent 1px)',
        backgroundSize: '32px 32px',
      }}
      data-testid="cockpit-stage"
      onClick={() => onDeselect && onDeselect()}
    >
      <div
        ref={stageInnerRef}
        style={{
          position: 'relative',
          ...stageStyle,
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
          transformOrigin: 'center center',
          transition: gestureActive ? 'none' : 'transform 150ms ease-out',
          // Unzoomed: allow the browser's native one-finger vertical scroll
          // and pinch-zoom gesture recognition to pass through (our own JS
          // still intercepts an actual 2-finger pinch via preventDefault,
          // taking over for the custom image zoom). Zoomed in: disable all
          // default touch behaviour so one-finger drag fully owns panning.
          touchAction: scale > 1.01 ? 'none' : 'pan-y pinch-zoom',
        }}
        onClick={handleStageClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onDoubleClick={handleDoubleClick}
        data-testid="cockpit-stage-inner"
      >
        <img
          src={`/cockpit/${view.image}`}
          alt={view.name}
          className="w-full h-full block rounded-md pointer-events-none"
          draggable={false}
          onLoad={handleImageLoad}
          data-testid="cockpit-stage-image"
        />
        {controls.map((c) => {
          const isSelected = c.id === selectedControlId;
          const isHighlighted = highlightIds.includes(c.id);
          const vFlip = c.y < 15 ? 'below' : 'above';
          const hAlign = c.x < 15 ? 'left' : c.x > 85 ? 'right' : 'center';
          const labelPos = {
            ...(vFlip === 'above' ? { bottom: 'calc(50% + 31px)' } : { top: 'calc(50% + 31px)' }),
            ...(hAlign === 'center'
              ? { left: '50%', transform: 'translateX(-50%)' }
              : hAlign === 'left'
              ? { left: '0', transform: 'translateX(0)' }
              : { right: '0', transform: 'translateX(0)' }),
          };

          return (
            <button
              key={c.id}
              ref={isSelected ? selectedMarkerRef : null}
              type="button"
              aria-label={c.name}
              aria-pressed={isSelected}
              onClick={(e) => {
                // Only reachable via keyboard (mouse/touch hit-testing is
                // disabled below via pointer-events-none, so the stage's
                // nearest-marker hit test handles pointer input instead).
                e.stopPropagation();
                if (c.id === selectedControlId) {
                  scrollSelectedIntoCenter();
                } else {
                  onSelect && onSelect(c.id);
                }
              }}
              className="absolute flex items-center justify-center pointer-events-none focus-visible:pointer-events-auto"
              style={{
                left: `${c.x}%`,
                top: `${c.y}%`,
                transform: 'translate(-50%, -50%)',
                width: '32px',
                height: '32px',
                zIndex: isSelected ? 3 : 2,
              }}
              data-testid={`cockpit-marker-${c.id}`}
            >
              <span
                className="rounded-full box-border"
                style={
                  isSelected
                    ? {
                        width: `${markerBaseSize + 12}px`,
                        height: `${markerBaseSize + 12}px`,
                        background: 'rgba(79,209,245,0.25)',
                        border: `2.5px solid var(--gc-cyan)`,
                        animation: 'cockpitMarkerPulse 1.6s ease-in-out infinite',
                      }
                    : isHighlighted
                    ? {
                        width: `${markerBaseSize + 4}px`,
                        height: `${markerBaseSize + 4}px`,
                        background: 'rgba(79,209,245,0.35)',
                        border: '2px solid #8FE3FA',
                      }
                    : {
                        width: `${markerBaseSize}px`,
                        height: `${markerBaseSize}px`,
                        background: 'rgba(7,11,17,0.75)',
                        border: `${markerBaseBorder}px solid var(--gc-cyan)`,
                      }
                }
              />
              {isSelected && (
                <span
                  className="font-plex-mono font-semibold whitespace-nowrap pointer-events-none absolute"
                  style={{
                    fontSize: '11px',
                    color: '#04161C',
                    background: 'var(--gc-cyan)',
                    borderRadius: '5px',
                    padding: '3px 7px',
                    zIndex: 4,
                    opacity: labelVisible ? 1 : 0,
                    transition: 'opacity 300ms',
                    ...labelPos,
                  }}
                  data-testid="cockpit-marker-callout"
                >
                  {c.name}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {scale > 1.01 && (
        <button
          type="button"
          onClick={resetZoom}
          className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1.5 rounded-gc-button text-xs font-medium"
          style={{ background: 'rgba(7,11,17,0.85)', border: '1px solid var(--gc-border-nav)', color: 'var(--gc-text-body)' }}
          data-testid="cockpit-reset-zoom-btn"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Reset zoom
        </button>
      )}
    </div>
  );
};

export default CockpitMarkerStage;
